import React, { useState, useEffect } from 'react';
import { getApiUrl } from "@/config/appConfig";
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { 
  Gift, Users, Clock, CheckCircle, DollarSign, Settings, 
  TrendingUp, Search, ChevronDown, ChevronUp, Trophy,
  AlertCircle, UserPlus, Wallet, RefreshCw, X
} from 'lucide-react';

// getApiUrl() already returns URL with /api
const API = getApiUrl();

export default function BackofficeReferrals() {
  const [referrals, setReferrals] = useState([]);
  const [stats, setStats] = useState({});
  const [settings, setSettings] = useState({});
  const [topReferrers, setTopReferrers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [referrerReward, setReferrerReward] = useState(50);
  const [referredReward, setReferredReward] = useState(50);
  const [newExpiryMonths, setNewExpiryMonths] = useState(6);
  const [programEnabled, setProgramEnabled] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      try {
        setLoading(true);
        const [referralsRes, settingsRes, topRes] = await Promise.all([
          axios.get(`${API}/referrals/admin/all${statusFilter ? `?status=${statusFilter}` : ''}`, { withCredentials: true }),
          axios.get(`${API}/referrals/admin/settings`, { withCredentials: true }),
          axios.get(`${API}/referrals/admin/top-referrers`, { withCredentials: true })
        ]);
        
        if (isMounted) {
          setReferrals(referralsRes.data.referrals || []);
          setStats(referralsRes.data.stats || {});
          const settingsData = settingsRes.data.settings || {};
          setSettings(settingsData);
          setReferrerReward(settingsData.referrer_reward || settingsData.reward_amount || 50);
          setReferredReward(settingsData.referred_reward || settingsData.reward_amount || 50);
          setNewExpiryMonths(settingsData.credit_expiry_months || 6);
          setProgramEnabled(settingsData.enabled !== false);
          setTopReferrers(topRes.data.top_referrers || []);
        }
      } catch (error) {
        console.error('Failed to fetch referral data:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchData();
    
    return () => {
      isMounted = false;
    };
  }, [statusFilter]);  // Only re-fetch when filter changes

  const refreshData = async () => {
    try {
      setLoading(true);
      const [referralsRes, settingsRes, topRes] = await Promise.all([
        axios.get(`${API}/referrals/admin/all${statusFilter ? `?status=${statusFilter}` : ''}`, { withCredentials: true }),
        axios.get(`${API}/referrals/admin/settings`, { withCredentials: true }),
        axios.get(`${API}/referrals/admin/top-referrers`, { withCredentials: true })
      ]);
      setReferrals(referralsRes.data.referrals || []);
      setStats(referralsRes.data.stats || {});
      const settingsData = settingsRes.data.settings || {};
      setSettings(settingsData);
      setReferrerReward(settingsData.referrer_reward || settingsData.reward_amount || 50);
      setReferredReward(settingsData.referred_reward || settingsData.reward_amount || 50);
      setNewExpiryMonths(settingsData.credit_expiry_months || 6);
      setProgramEnabled(settingsData.enabled !== false);
      setTopReferrers(topRes.data.top_referrers || []);
    } catch (error) {
      console.error('Failed to fetch referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSavingSettings(true);
      await axios.put(`${API}/referrals/admin/settings`, {
        referrer_reward: referrerReward,
        referred_reward: referredReward,
        credit_expiry_months: newExpiryMonths,
        enabled: programEnabled
      }, { withCredentials: true });
      await refreshData();
      setShowSettings(false);
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('Failed to save settings: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setSavingSettings(false);
    }
  };

  const approveReferral = async (referralId) => {
    if (!window.confirm('Approve this referral and award credit to both parties?')) return;
    try {
      await axios.post(`${API}/referrals/admin/approve/${referralId}`, {}, { withCredentials: true });
      await refreshData();
    } catch (error) {
      console.error('Failed to approve referral:', error);
      alert(error.response?.data?.detail || 'Failed to approve referral');
    }
  };

  const rejectReferral = async (referralId) => {
    const reason = window.prompt('Enter rejection reason:');
    if (!reason) return;
    try {
      await axios.post(`${API}/referrals/admin/reject/${referralId}?reason=${encodeURIComponent(reason)}`, {}, { withCredentials: true });
      await refreshData();
    } catch (error) {
      console.error('Failed to reject referral:', error);
      alert('Failed to reject referral');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Referral Program Management</h1>
          <p className="text-gray-500 mt-1">Manage referrals, rewards, and program settings</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={refreshData}>
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
          <Button onClick={() => setShowSettings(!showSettings)}>
            <Settings className="h-4 w-4 mr-2" /> Settings
          </Button>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <Card className="border-violet-200 bg-violet-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Program Settings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-6">
              <div>
                <Label>Referrer Reward (£)</Label>
                <Input
                  type="number"
                  value={referrerReward}
                  onChange={(e) => setReferrerReward(parseFloat(e.target.value) || 0)}
                  min="0"
                  step="5"
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">What the referrer gets</p>
              </div>
              <div>
                <Label>Referred Reward (£)</Label>
                <Input
                  type="number"
                  value={referredReward}
                  onChange={(e) => setReferredReward(parseFloat(e.target.value) || 0)}
                  min="0"
                  step="5"
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">What the new user gets</p>
              </div>
              <div>
                <Label>Credit Expiry (months)</Label>
                <Input
                  type="number"
                  value={newExpiryMonths}
                  onChange={(e) => setNewExpiryMonths(parseInt(e.target.value) || 6)}
                  min="1"
                  max="24"
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">Months until credit expires</p>
              </div>
              <div>
                <Label>Program Status</Label>
                <div className="flex items-center gap-2 mt-2">
                  <Switch
                    checked={programEnabled}
                    onCheckedChange={setProgramEnabled}
                  />
                  <span className={programEnabled ? 'text-green-600' : 'text-gray-500'}>
                    {programEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={saveSettings} disabled={savingSettings}>
                {savingSettings ? 'Saving...' : 'Save Settings'}
              </Button>
              <Button variant="outline" onClick={() => setShowSettings(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Total Referrals</p>
                <p className="text-2xl font-bold">{stats.total || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-2xl font-bold">{stats.pending || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Confirmed</p>
                <p className="text-2xl font-bold">{stats.confirmed || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <div className="p-2 bg-violet-100 rounded-lg">
                <Wallet className="h-5 w-5 text-violet-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Total Credits Awarded</p>
                <p className="text-2xl font-bold">£{(stats.total_credits_awarded || 0).toFixed(0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Referrers */}
      {topReferrers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-amber-500" />
              Top Referrers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-5 gap-4">
              {topReferrers.slice(0, 5).map((referrer, idx) => (
                <div key={referrer._id} className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className={`w-10 h-10 rounded-full mx-auto mb-2 flex items-center justify-center font-bold text-white ${
                    idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-amber-700' : 'bg-gray-300'
                  }`}>
                    {idx + 1}
                  </div>
                  <p className="font-medium text-sm truncate">{referrer.referrer_email}</p>
                  <p className="text-xs text-gray-500">{referrer.referrer_name || 'Unknown'}</p>
                  <div className="mt-2">
                    <span className="text-lg font-bold text-violet-600">{referrer.confirmed_count}</span>
                    <span className="text-xs text-gray-500 ml-1">referrals</span>
                  </div>
                  <p className="text-xs text-green-600">£{referrer.total_earned} earned</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Referrals Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>All Referrals</CardTitle>
            <div className="flex gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border rounded-md px-3 py-1 text-sm"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {referrals.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No referrals found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2 text-sm font-medium text-gray-500">Referrer</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-gray-500">Referred</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-gray-500">Code</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-gray-500">Created</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-gray-500">Status</th>
                    <th className="text-right py-3 px-2 text-sm font-medium text-gray-500">Reward</th>
                    <th className="text-right py-3 px-2 text-sm font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map((ref) => (
                    <tr key={ref.referral_id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="py-3 px-2">
                        <div>
                          <p className="font-medium">{ref.referrer_email}</p>
                          <p className="text-xs text-gray-500">{ref.referrer_name || '-'}</p>
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <span className="font-medium">{ref.referred_email}</span>
                      </td>
                      <td className="py-3 px-2">
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded">{ref.referral_code}</code>
                      </td>
                      <td className="py-3 px-2 text-sm text-gray-500">
                        {formatDate(ref.created_at)}
                      </td>
                      <td className="py-3 px-2">
                        {ref.status === 'pending' && (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                            <Clock className="h-3 w-3 mr-1" /> Pending
                          </Badge>
                        )}
                        {ref.status === 'confirmed' && (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            <CheckCircle className="h-3 w-3 mr-1" /> Confirmed
                          </Badge>
                        )}
                        {ref.status === 'rejected' && (
                          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                            <X className="h-3 w-3 mr-1" /> Rejected
                          </Badge>
                        )}
                      </td>
                      <td className="py-3 px-2 text-right">
                        <span className="font-medium">£{ref.reward_amount}</span>
                      </td>
                      <td className="py-3 px-2 text-right">
                        {ref.status === 'pending' && (
                          <div className="flex gap-1 justify-end">
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-green-600 hover:bg-green-50"
                              onClick={() => approveReferral(ref.referral_id)}
                            >
                              <CheckCircle className="h-3 w-3 mr-1" /> Approve
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => rejectReferral(ref.referral_id)}
                            >
                              <X className="h-3 w-3 mr-1" /> Reject
                            </Button>
                          </div>
                        )}
                        {ref.status === 'confirmed' && (
                          <span className="text-xs text-gray-500">
                            {formatDate(ref.confirmed_at)}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Current Settings Display */}
      <Card className="bg-gray-50">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">Current Program Settings</h4>
              <p className="text-sm text-gray-500 mt-1">
                Referrer: <strong className="text-violet-600">£{settings.referrer_reward || settings.reward_amount || 50}</strong> | 
                Referred: <strong className="text-green-600">£{settings.referred_reward || settings.reward_amount || 50}</strong> | 
                Expiry: <strong>{settings.credit_expiry_months || 6} months</strong> | 
                Status: <strong className={settings.enabled !== false ? 'text-green-600' : 'text-red-600'}>
                  {settings.enabled !== false ? 'Enabled' : 'Disabled'}
                </strong>
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowSettings(true)}>
              <Settings className="h-4 w-4 mr-1" /> Edit
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
